import { DetailResponseData, FieldResponseDTO } from 'features/forms/types';

interface TemplateOptions {
  t: (key: string) => string;
  formatDate: (date: string) => string;
}

interface ApprovalItem {
  id: string;
  fullName: string;
  fonctionName: string;
  image: string;
  level: string;
  date: string | null;
  statut: {
    text: string | null;
  };
  status: string | null;
  raison: string | null;
}

export const generateResponseHTML = (
  responseData: DetailResponseData,
  options: TemplateOptions,
): string => {
  const { response, clientName } = responseData;
  const { t, formatDate } = options;

  const fieldsHTML = response.fields
    .sort((a: FieldResponseDTO, b: FieldResponseDTO) => a.order - b.order)
    .map((field: FieldResponseDTO) => {
      return `
        <div class="form-field">
          <span class="info-label">
            ${field.title}
          </span> :
          <span class="field-value">
            ${field.response || '-'}
          </span>
        </div>
      `;
    })
    .join('');

  // Generate approval section HTML if exists
  const approvalHTML =
    response.approval && response.approval.length > 0
      ? `
      <div class="section">
        <div class="section-title">2 - ${t('Form.ApprovalProcess')}</div>
        <div class="approval-item-container">
          ${response.approval
            .map(
              (approval: ApprovalItem) => `
              <div class="approval-item">
                <table class="approval-layout">
                  <tr>
                    <td class="approver-info">
                      <div class="approver-name">${approval.fullName}</div>
                      <div class="approver-department">${t(approval.fonctionName)}</div>
                      ${
                        approval.raison
                          ? `
                        <div style="margin-top: 5px; font-size: 11px; color: #666;">
                          <strong>${t('comment')}:</strong> ${approval.raison}
                        </div>
                      `
                          : ''
                      }
                    </td>
                    <td class="approval-details">
                      <div>
                        <span class="status-badge">
                          ${t(approval.statut.text || '')}
                        </span>
                      </div>
                      <div style="padding-top: 5px;">
                        ${approval.date ? formatDate(approval.date) : '-'}
                      </div>
                    </td>
                  </tr>
                </table>
              </div>
            `,
            )
            .join('')}
        </div>
      </div>
    `
      : '';

  return `
<!DOCTYPE html>
<html>
<head>
    <meta charset="utf-8" />
    <title>${response.formName || ''} - Form Report</title>
    <style>
        * {
            margin: 0;
            padding: 0;
        }

        body {
            font-family: Arial, sans-serif;
            font-size: 12px;
            line-height: 1.4;
            color: #333;
            background: white;
            max-width: 210mm;
            margin: 0 auto;
            padding: 20mm;
        }

        .header {
            text-align: center;
            margin-bottom: 30px;
            border-bottom: 2px solid #2C7955;
            padding-bottom: 20px;
        }

        .company-logo {
            font-size: 24px;
            font-weight: bold;
            color: #2C7955;
            margin-bottom: 10px;
        }

        .report-title {
            font-size: 18px;
            font-weight: bold;
            color: #333;
            margin: 10px 0;
        }

        .section {
            margin-bottom: 25px;
        }

        .section-title {
            background-color: #2C7955;
            color: white;
            padding: 8px 12px;
            font-weight: bold;
            font-size: 14px;
            line-height: 0.8;
        }

        .info-grid {
            width: 100%;
            border-collapse: collapse;
            background: #f8f9fa;
            padding: 10px;
            padding-top: 5px;
        }

        .info-grid td {
            width: 50%;
            padding: 5px 5px 5px 5px;
            vertical-align: top;
        }

        .info-grid td:last-child {
            padding-right: 0;
        }

        .info-label {
            font-weight: bold;
            color: #666;
            margin-bottom: 3px;
            padding-bottom: 5px;
            font-size: 11px;
            text-transform: uppercase;
        }

        .info-value {
            font-size: 13px;
            color: #333;
            border-bottom: 1px solid #eee;
            padding-bottom: 3px;
        }

        .status-badge {
            display: inline-block;
            padding: 4px 8px;
            border: 1px solid #ccc;
            font-size: 11px;
            font-weight: bold;
            text-transform: uppercase;
        }

        .form-field-responses {
            padding: 10px;
            padding-top: 5px;
            background: #f8f9fa;
        }

        .form-field {
            margin-bottom: 5px;
            padding: 5px;
            border: 1px solid #e9ecef;
        }

        .field-label {
            font-weight: bold;
            color: #495057;
            margin-bottom: 8px;
            font-size: 13px;
        }

        .field-value {
            color: #333;
            font-size: 12px;
            line-height: 1.5;
            word-wrap: break-word;
        }

        .approval-item-container {
            background: #f8f9fa;
            padding: 10px;
            padding-top: 5px;
        }

        .approval-item {
            margin-bottom: 5px;
            padding: 5px;
            width: 100%;
        }

        .approval-layout {
            width: 100%;
            border-collapse: collapse;
        }

        .approval-layout td {
            padding: 0;
            vertical-align: top;
        }

        .approver-info {
            width: 70%;
        }

        .approval-details {
            width: 30%;
            text-align: right;
        }

        .approver-name {
            font-weight: 400;
            color: #333;
            margin-bottom: 2px;
            font-size: 0.85em;
            padding-bottom: 5px;
        }

        .approver-department {
            color: #666;
            font-size: 11px;
        }

        .approval-details {
            font-size: 11px;
            color: #666;
        }

        .footer {
            margin-top: 40px;
            padding-top: 20px;
            border-top: 1px solid #ddd;
            text-align: center;
            color: #666;
            font-size: 10px;
        }
    </style>
</head>
<body>
    <div class="header">
        <div class="company-logo">${clientName}</div>
        <div class="report-title">${response.formName || ''}</div>
    </div>

    <div class="section">
        <div class="section-title">1 - ${t('Form.GeneralInformation')}</div>
        <table class="info-grid">
            <tr>
                <td>
                    <div class="info-label">${t('Form.SubmittedBy')}</div>
                    <div class="info-value">${response.user?.text || t('Form.Anonymous.User')}</div>
                </td>
                <td>
                    <div class="info-label">${t('status')}</div>
                    <div class="info-value">
                        ${t(response.statut?.text || '')}
                    </div>
                </td>
            </tr>
            <tr>
                <td>
                    <div class="info-label">${t('Form.SubmissionDate')}</div>
                    <div class="info-value">${formatDate(response.createdDate)}</div>
                </td>
            </tr>    
        </table>
    </div>

    <div class="section">
        <div class="section-title">3 - ${t('Form.Responses')}</div>
        <div class="form-field-responses">
            ${fieldsHTML}
        </div>
    </div>

    ${approvalHTML}

    <div class="footer">
        <div style="margin-top: 10px;">
            <div>${t('Form.Report.GeneratedDate')}: <span id="generated-date">${formatDate(new Date().toISOString())}</span></div>
        </div>
    </div>
</body>
</html>
  `;
};
